// F2 `CallControlApi` over the workspace's ApiClient, so the engine shares the same
// cookie session, CSRF token, 401 handling and error envelope as the rest of the app.
import type { Call, CallAction, CommandReceipt, CreateCall, DiagnosticReport } from '@bcio-voice/contracts';
import type { ApiResult, CallControlApi, DeviceHeartbeat } from '@bcio-voice/call-client/core';
import { type ApiClient, type ApiRequestOptions, isApiRequestError } from '../lib/api.ts';

// Structural guards for the fields the engine relies on (the server stays the schema authority).
// Local so the engine itself loads lazily with SIP.js instead of in the first page chunk.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CALL_STATES = new Set(['created', 'ringing', 'active', 'held', 'dropping', 'ended', 'failed']);
const COMMAND_STATES = new Set(['accepted', 'dispatching', 'applied', 'failed', 'unknown']);

export function isCall(value: unknown): value is Call {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === 'string' && UUID.test(v.id) && Number.isInteger(v.version) && (v.version as number) >= 1
    && typeof v.state === 'string' && CALL_STATES.has(v.state) && typeof v.direction === 'string' && typeof v.recording_state === 'string'
    && Array.isArray(v.participants) && Array.isArray(v.allowed_actions) && (v.allowed_actions as unknown[]).every(a => typeof a === 'string');
}

export function isCommandReceipt(value: unknown): value is CommandReceipt {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.command_id === 'string' && UUID.test(v.command_id) && typeof v.status === 'string' && COMMAND_STATES.has(v.status) && isCall(v.call);
}

export interface EngineApi extends CallControlApi {
  /** B1 `POST /devices/{id}/credentials`: returns the canonical Device. Every call rotates the secret. */
  issueCredentials(deviceId: string, signal?: AbortSignal): Promise<ApiResult<unknown>>;
}

export function createEngineApi(api: Pick<ApiClient, 'request'>): EngineApi {
  async function call<T>(path: string, init: ApiRequestOptions, guard: ((value: unknown) => value is T) | null): Promise<ApiResult<T>> {
    let body: { data?: unknown } | undefined;
    try {
      body = await api.request<{ data?: unknown } | undefined>(path, init);
    } catch (error) {
      if (isApiRequestError(error) && error.status > 0) {
        return { ok: false, kind: 'http', status: error.status, code: error.code, message: error.message, retryable: error.retryable, request_id: error.requestId };
      }
      return { ok: false, kind: 'network', message: 'The server could not be reached.' };
    }
    const data = body?.data;
    if (guard && !guard(data)) return { ok: false, kind: 'invalid_response', status: 200, message: 'Response did not match the expected resource.' };
    return { ok: true, value: data as T };
  }
  const post = (body: unknown, key: string | null, signal?: AbortSignal): ApiRequestOptions => ({
    method: 'POST',
    body: JSON.stringify(body),
    ...(key ? { idempotencyKey: key } : {}),
    ...(signal ? { signal } : {}),
  });
  const get = (signal?: AbortSignal): ApiRequestOptions => (signal ? { signal } : {});
  const id = (value: string) => encodeURIComponent(value.toLowerCase());
  return {
    createCall: (body: CreateCall, key: string, signal?: AbortSignal) => call<CommandReceipt>('/calls', post(body, key, signal), isCommandReceipt),
    callAction: (callId: string, body: CallAction, key: string, signal?: AbortSignal) => call<CommandReceipt>(`/calls/${id(callId)}/actions`, post(body, key, signal), isCommandReceipt),
    getCommand: (commandId: string, signal?: AbortSignal) => call<CommandReceipt>(`/commands/${id(commandId)}`, get(signal), isCommandReceipt),
    getCall: (callId: string, signal?: AbortSignal) => call(`/calls/${id(callId)}`, get(signal), isCall),
    heartbeat: (deviceId: string, body: DeviceHeartbeat, signal?: AbortSignal) => call<unknown>(`/devices/${id(deviceId)}/heartbeat`, post(body, null, signal), null),
    reportDiagnostics: (callId: string, report: DiagnosticReport, signal?: AbortSignal) => call<unknown>(`/calls/${id(callId)}/diagnostics`, post(report, null, signal), null),
    issueCredentials: (deviceId: string, signal?: AbortSignal) => call<unknown>(`/devices/${id(deviceId)}/credentials`, { method: 'POST', ...(signal ? { signal } : {}) }, null),
  };
}
